/**
 * TaskChangeCard - Card for task entity changes.
 *
 * Displays task name, group, type, due date, description,
 * and nested task updates.
 */

import { ClipboardList } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { CoverageLink, MedicalProviderLink, PoliceReportLink, UserLink } from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { formatEnumValue } from '../utils/formatting';
import { useChangedFields } from '../utils/useChangedFields';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { CollapsibleSection } from './CollapsibleSection';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TaskUpdateChangeCard } from './TaskUpdateChangeCard';

const KEY_FIELDS: FieldConfig[] = [
  { key: 'group', label: 'Group' },
  { key: 'type', label: 'Type' },
  { key: 'due_date', label: 'Due Date', isDate: true },
];

const REFERENCE_FIELDS: FieldConfig[] = [];

interface TaskChangeCardProps {
  entity: EntityChangeSchema;
  taskUpdates?: EntityChangeSchema[];
  matterId?: string;
  /** Called when the task name is clicked (e.g. to open task detail). */
  onNameClick?: () => void;
}

export const TaskChangeCard: React.FC<TaskChangeCardProps> = ({
  entity,
  taskUpdates = [],
  matterId,
  onNameClick,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const taskName = (data.name as string) || 'Unnamed Task';
  const group = data.group as string | undefined;
  const taskType = data.type as string | undefined;
  const description = (data.description as string) || '';
  const coverageId =
    typeof data.coverage_id === 'string' && data.coverage_id
      ? data.coverage_id
      : null;

  const cardRef = useRef<HTMLDivElement>(null);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);

  useEffect(() => {
    if (!cardRef.current || hasEnteredViewport) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [hasEnteredViewport]);

  const shouldFetchCoverageName = Boolean(
    matterId && coverageId && hasEnteredViewport,
  );

  const { data: coverage, isFetching: isFetchingCoverage } = useGetCoverageQuery(
    {
      matterId: matterId ?? '',
      coverageId: coverageId ?? '',
    },
    { skip: !shouldFetchCoverageName },
  );

  const insuranceCompanyName = coverage?.carrier?.name?.trim() || null;

  const changedUpdates = taskUpdates.filter(
    (tu) => tu.change_type !== 'ignored',
  );
  const unchangedUpdates = taskUpdates.filter(
    (tu) => tu.change_type === 'ignored',
  );

  // Auto-expand if there are nested task updates with actual changes
  const hasNestedChanges = changedUpdates.length > 0;
  const shouldExpand = hasNestedChanges ? true : undefined;

  return (
    <div ref={cardRef}>
      <EntityChangeCardShell
        changeType={entity.change_type}
        name={taskName}
        description={description.length > 80 ? `${description.slice(0, 80)}…` : description}
        entityStatus={effectiveStatus}
        defaultExpanded={shouldExpand}
        onStatusChange={
          actions
            ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
            : undefined
        }
        onNameClick={onNameClick}
        icon={ClipboardList}
        iconBgClassName="bg-violet-50"
        iconTextClassName="text-violet-600"
        badges={
          <>
            {group && (
              <Badge color="zinc" className="text-xs">
                {group}
              </Badge>
            )}
            {taskType && (
              <Badge color="zinc" className="text-xs">
                {formatEnumValue(taskType)}
              </Badge>
            )}
          </>
        }
        changeCount={changes.totalChanges}
        hasExpandedContent
      >
      {/* Description (if changed) */}
      {changes.isFieldChanged('description') && (
        <ChangedFieldHighlight
          label="Description"
          fieldKey="description"
          changes={changes}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {description}
          </p>
        </ChangedFieldHighlight>
      )}

      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Nested task updates */}
      {taskUpdates.length > 0 && (
        <CollapsibleSection
          title="Task Updates"
          count={taskUpdates.length}
          changeType={entity.change_type}
          hasChanges={changedUpdates.length > 0}
        >
          {changedUpdates.map((tu) => (
            <TaskUpdateChangeCard key={tu.id} entity={tu} condensed matterId={matterId} />
          ))}
          {unchangedUpdates.length > 0 && (
            <ShowMoreSection changeType={entity.change_type}>
              {unchangedUpdates.map((tu) => (
                <TaskUpdateChangeCard key={tu.id} entity={tu} condensed matterId={matterId} />
              ))}
            </ShowMoreSection>
          )}
        </CollapsibleSection>
      )}

      {/* Entity links for assignee, created by, coverage */}
      {matterId && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
          {typeof data.assignee_id === 'string' && data.assignee_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Assignee:</span>
              <UserLink entityId={data.assignee_id} className="text-sm font-normal">
                Assignee
              </UserLink>
            </div>
          )}
          {typeof data.created_by_id === 'string' && data.created_by_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Created By:</span>
              <UserLink entityId={data.created_by_id} className="text-sm font-normal">
                Created By
              </UserLink>
            </div>
          )}
          {coverageId && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Coverage:</span>
              <CoverageLink entityId={coverageId} matterId={matterId} className="text-sm font-normal">
                Coverage
              </CoverageLink>
              {insuranceCompanyName && (
                <span className="text-xs text-gray-500 truncate">
                  {insuranceCompanyName}
                </span>
              )}
              {!insuranceCompanyName && shouldFetchCoverageName && isFetchingCoverage && (
                <span className="text-xs text-gray-400">Loading insurer...</span>
              )}
            </div>
          )}
          {typeof data.police_report_id === 'string' && data.police_report_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Police Report:</span>
              <PoliceReportLink entityId={data.police_report_id} matterId={matterId} className="text-sm font-normal">
                Police Report
              </PoliceReportLink>
            </div>
          )}
          {typeof data.medical_note_id === 'string' && data.medical_note_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Provider:</span>
              <MedicalProviderLink entityId={data.medical_note_id} matterId={matterId} className="text-sm font-normal">
                Provider
              </MedicalProviderLink>
            </div>
          )}
        </div>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        {!changes.isFieldChanged('description') && description && (
          <div className="px-1 py-0.5">
            <span className="text-xs text-gray-400">Description:</span>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
              {description}
            </p>
          </div>
        )}
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGrid
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
      </EntityChangeCardShell>
    </div>
  );
};
